/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { fetchData, postData, putData, deleteData, patchData } from '@jda/lui-portal-utilities';
import { getAuthToken } from '../auth/authProvider';
import { ISetting, ISettingsPayload } from '../models/Settings/SettingContract';
import { createRequestObject, convertObjectToQueryString, getAPIMUrl } from '../utility/commonUtility';
const { CLIENT_SERVICE_V1_URL, ENTITLEMENT_SERVICE, PASSWORD_POLICY_SERVICE, IDENTITY_SERVICE, ENTITLEMENT_SERVICE_V2 } = window['env'];

export const getUserRealmSettings = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/realmSettings`;
  console.log("api URL ---->", apiURL);
  return fetchData<ISetting>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getRealmInfo = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${ENTITLEMENT_SERVICE_V2}/realms/info`;

  return fetchData(createRequestObject(apiURL), getAuthToken).then(appsResponse => {
    return appsResponse;
  }).catch(error => {
    throw error;
  });
};


export const createRealmSettings = async (data: ISettingsPayload) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/realmSettings`;
  return postData<ISetting>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const updateRealmSettings = async (data: ISettingsPayload) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/realmSettings`;
  return putData(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getIdpById = async (idpId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${IDENTITY_SERVICE}/idp/${idpId}`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken);
};

export const toggleMfa = async (enabled: boolean) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${IDENTITY_SERVICE}/mfa`;
  return putData(createRequestObject(apiURL), { enabled });
};

export const createIdp = async (data: { identityProviderType: string; locationId?: string }) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${ENTITLEMENT_SERVICE}/identityProviders`;
  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
export const createIdpForGravitee = async (data: { type: string; locationId?: string }) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${IDENTITY_SERVICE}/idp`;
  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
export const updateIdp = async (idpId: string, data: { enabled: boolean, b2cIdpId?: string }) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${IDENTITY_SERVICE}/idp/${idpId}`;
  return patchData(createRequestObject(apiURL), data, getAuthToken);
};

export const getAllPasswordPolicy = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse || [];
    })
    .catch((error: any) => {
      throw error;
    });
};
export const getPasswordPolicyRule = async (policyId: string, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies/${policyId}`;
  return fetchData<any>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getPasswordPolicyLocations = async (
  policyId: string,
  queryParams?: { pageNumber?: number; pageSize?: number; sortBy?: string }
) => {
  let queryData = '';
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies/${policyId}/locations`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return await fetchData<any>(createRequestObject(apiURL), getAuthToken);
};

export const setDefaultPasswordPolicy = async (policyId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies/${policyId}/default`;
  return postData<any>(createRequestObject(apiURL), getAuthToken);
};

export const createPasswordPolicyRule = async (data: any) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies`;
  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const updatePasswordPolicyRule = async (policyId: string, data: any) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies/${policyId}`;
  return patchData(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const assignPasswordPolicyToRealm = async (idpId: string, policyId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/idps/${idpId}/policy`;
  return putData<any>(createRequestObject(apiURL), { passwordPolicy: policyId }, getAuthToken);
};

export const deletePasswordPolicy = async (policyId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies/${policyId}`;
  return deleteData(createRequestObject(apiURL), getAuthToken);
};

export const assignPasswordPolicyToIdps = async (policyId: string, idpIds: string[]) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${PASSWORD_POLICY_SERVICE}/policies/${policyId}/idps`;
  return putData<any>(createRequestObject(apiURL), idpIds, getAuthToken);
};
